import { useState, useMemo, useEffect, useCallback, forwardRef, useRef, useImperativeHandle, MutableRefObject, useLayoutEffect } from 'react';
import { useParams } from 'react-router-dom';

import { AgGridReact, CustomNoRowsOverlayProps } from "@ag-grid-community/react";
import { GridReadyEvent, IRichCellEditorParams } from 'ag-grid-community';
import { GridChartsModule } from '@ag-grid-enterprise/charts';
import { ClipboardModule } from '@ag-grid-enterprise/clipboard';
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping';
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { MenuModule } from "@ag-grid-enterprise/menu";
import { isNil, isString } from 'lodash-es';
import { BodySmall, Box, Snackbar, TableHeaderMenuIcon, IconButton, HistoryOutlinedIcon, Portal } from '@parspec/pixel';

import {
    CellValueChangedEvent,
    ColDef,
    ICellRendererParams,
    ProcessCellForExportParams,
    ColumnResizedEvent,
    ColumnMovedEvent,
    ColumnState,
    GetQuickFilterTextParams,
    RowClassParams,
    ValueGetterParams,
    ValueSetterParams,
    IRowNode,
    CellClassParams,
    ColSpanParams,
    SuppressKeyboardEventParams,
    GetContextMenuItemsParams,
} from '@ag-grid-community/core';

import { SpecificationDropdown } from '../../shared/SpecificationDropdown';
import { BOM_TABLES_ENUM, SpecificationValType, ROW_LEVEL, ROW_HEIGHT } from '../../shared/constants';
import DeleteConfirmationDialog from '../../../shared/DeleteConfirmationDialog';
import {
    deleteTitle,
    deleteMessage,
    deleteDialogTitle,
    getCellAmountFormatter,
    getCellPercentFormatter,
    pricingCellParams,
    TextCellParams,
    getRowId,
    getValidatedLeadTimeRange,
    getFormattedLeadTimeRange,
    DELETE_SECTION_TITLE,
    suppresskeyboardEventForEnter,
} from '../../shared/utils';
import {
    getColorHandlerForMainTable,
    getFooterDisabled,
    getTemplateCellStyle,
    getValueGetterForPricingCols,
    isLeadTimeValueEditable,
    pricingFieldToolTipValueGetter,
    DISABLED_COLOR_HEX,
    handleProcessCellPasteForPricing,
    isSectionEditingNotAllowed,
    isDisabledCellClassRules,
    isPricingFieldsEditable,
    aggFunctions
} from '../shared/utils';
import { MainProductComponentsDataType, MainProductDataType } from './types';
import { bomCols, priceRelatedCols, pricingBodInfoCols, LEAD_TIME_UNITS, toolBaroptions, COMPONENT_LOTS_TOOLTIP_MESSAGE, LEAD_TIME_VALUE_TOOLTIP_MSG, BULK_EDIT_OPTIONS } from '../shared/constants';
import MoveSectionDialog from '../../shared/MoveSectionDialog';
import { SectionApiResponseType } from '../../queries/apiTypes';
import {
    saveColumnOrderEvents,
    saveColumnWidthEvents,
    useGetBomRoundingPresetQuery
} from '../../queries';

import { getExtendedCostFormatter, getExtendedSellPriceFormatter, getValueSetterForMainProductPricingCols } from './utils';
import { KIT_ERROR } from '../../shared/constants';
import { MenuOptionsContext, TableContext } from '../../shared/context';
import { MenuOptions } from '../../shared/MenuOptions';
import ErrorDialog from '../../../shared/ErrorDialog';
import BulkEditModal, { EditOptionsT } from '../bulkEdit/bulkEditPopup';
import { LotTemplate } from './lotTemplate';
import CustomToolBarPanel from '../../../shared/CustomToolBarPanel';
import { CustomHeader, getSectionalValueTemplate, LoadingOverlayComponent, NoRowsOverlayWithButtons, PerCustomHeader, ProductFinderDisabledTemplate, ProductFinderTemplate, QtyTemplate, } from '../../shared/commonTemplates';
import { useLotsObj } from './hooks';
import PricingHistoryModal from '../PricingHistoryModal';
import { PricingLeadTimeUnitTemplate } from '../shared/LeadTimeUnitTemplate';
import MoveProductToKitDialog from '../../shared/MoveProductToKitDialog';
import { useGetCustomisedColumnData } from '../../shared/hooks';
import { ProductToReplaceTemplate } from '../../shared/ProductToReplaceTemplate';
import { notifyToSentry } from '../../../shared/SentryErrorLogger';
import { SectionAndBomItemData } from '../../shared/types';
import PerTemplate from '../shared/PerTemplate';
import { useCheckAccessForProductFinderOrQuoting } from 'src/features/shared/hooks/useCheckAccessForProductFinderOrQuoting';
import { CustomToolTipForTable } from '../../shared/CustomTooltipForTable';
import SelectSectionModal from '../../shared/SelectSectionModal';
import AddOptionModal from '../../shared/addOptionModal';
import { useKitPricingUpdate } from '../../shared/hooks/useKitPricingUpdate';
import { getTableRightClickOptions } from '../../shared/TableRightClickOptions';
import ViewBySelect, { ViewOptionValues } from '../shared/ViewBySelect';
import { useBomTableActionHandlers } from '../../shared/hooks/useBomTableActionsHandlers';
import AutocompleteEditor from '../../shared/AutoCompleteCellEditor';
import { isValidCharactersForMfg } from 'src/features/Settings/Manufacturer/utils';
import { INVALID_CHARACTERS_IN_MFG_NAME } from 'src/features/Settings/Manufacturer/constants';

const modules = [ClientSideRowModelModule, ClipboardModule, GridChartsModule, ColumnsToolPanelModule, RowGroupingModule, MenuModule];

interface MainProductTableProps {
    tableData: MainProductDataType[];
    onPricingInfoEdit: (info: Partial<Omit<MainProductDataType, 'qty' | 'category' | 'manufacturer' | 'model_number' | 'specification' | 'lotInfo'>>, shouldCancelQueries?: boolean) => Promise<void>;
    onBomInfoEdit: (info: Partial<Pick<MainProductDataType, 'qty' | 'category' | 'manufacturer' | 'model_number' | 'specification' | 'id'>>) => Promise<void>;
    onLotUpdate?: (args: { bodId: number; prevLotId: string; currLotId: string; componentIds?: Set<number> }) => void;
    onNewLot?: () => void;
    isLoading: boolean;
    optionSection?: SectionApiResponseType[];
    tableMaxHeight: number;
    mainProductOptionsInfo?: Record<number, Array<number>>;
    toggleBulkEditBanner?: () => void;
    onGridReady?: (params: GridReadyEvent) => void;
    handleTablesReordering?: (columnOrder: string[]) => void;
    handleTablesWidthChanged?: (targetedColumnId: string, newWidth?: number) => void;
    selectedRowsRef: MutableRefObject<Set<number>>;
    getSectionBodItems?: (sectionId: number, isOptionTable?: boolean) => SectionAndBomItemData[]
    currentGroupWhilePastingRef: React.MutableRefObject<number | null>;
    isOptionTable?: boolean;
    currentTabValue: string | null;
    onChangeTableView: (value: ViewOptionValues) => void;
}

const defaultColDef: ColDef = {
    sortable: false,
    menuTabs: [],
    resizable: true,
    cellStyle: getFooterDisabled,
    suppressFillHandle: true,
    editable: (params) => !params.node.rowPinned,
    tooltipComponent: CustomToolTipForTable,
    suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
        if (params.event.key === "Enter") return suppresskeyboardEventForEnter(params);
        return false;
    },
};

//TODO: change the ref type 
const MainProductTable = forwardRef(function MainProductTable(props: MainProductTableProps, ref: any) {
    const {
        tableData,
        onNewLot: handleNewLot,
        onBomInfoEdit: handleBomInfoEdit,
        onPricingInfoEdit: handlePricingInfoEdit,
        onLotUpdate: handleLotUpdate,
        isLoading,
        tableMaxHeight,
        optionSection,
        mainProductOptionsInfo,
        isOptionTable = false,
        handleTablesReordering,
        handleTablesWidthChanged,
        selectedRowsRef,
        getSectionBodItems,
        currentGroupWhilePastingRef,
        onChangeTableView,
        currentTabValue
    } = props;
    const { bomId = '0' } = useParams();
    const tableRef = useRef<AgGridReact>(null);

    const [isPricingHistoryModalOpen, setPricingHistoryModalOpen] = useState(false);
    const [pricingHistoryInfo, setPricingHistoryInfo] = useState<{
        type?: string;
        manufacturer?: string;
        modelNo?: string;
        id?: number;
        cost?: number;
        discount?: number;
        discountedCost?: number;
        extendedDiscountCost?: number;
        sellPrice?: number;
        margin?: number;
        qty?: number;
        extendedPrice?: number;
        lotId?: number;
        kitLotId?: number | null;
    }>();
    const { handleKitPricingUpdate } = useKitPricingUpdate(bomId);
    const { onDuplicate, onAddKitLine, onAddBelow, onCopyProductDataToKit, onHighlight, onProductMoveOutFromKit, onProductMoveToKit, onClickDelete, onClickMoveSection, onAddOption, handleAddOptionForMultipleOptions, onHide, onMoveMultipleItems, onHideMultiple, handleMoveProductDialog, rowDragTextCallback, handleToggleAddOptionModal, onCellKeyDown, handleOnGridReady, onCloseBanner, onHighlightMultiple, onRowGroupOpened, toggleKits, setKitsExpansionObj, onDuplicateMultiple, handleOnRowDataUpdated, closeConfirmationDialog, onClickMultipleDelete, onDeleteBulk, toggleMoveSectionDialog, onRowDragEnd, onRowDragLeave, onRowDragMove, onCheckboxChange, handleSectionNameUpdate, onCreateSectionForEmptyTable, onHandleUnpinnedColumns, handleClearSelection, onCreateKitWithoutLineItems, onCreateKitWithSelectedLineItems, updateKitCreationError, handleToggleSectionModal, handleRangeSelectionChanged, handleOnPasteEnd, handleSearch, getTableMetaData, setSnackbarMessage, onCellContextMenu, onCellFocused, setSelectedItems, createNewRowsFromClipboard, addNewRowsToTheTable, suppressKeyboardEvent, valueGetterForMfg, handleKitPricingCallback, companyMfgData, autoGroupColumnDef, snackbarMessage, isTableLoadingAfterMutation, isHoverCellClassRules, isAddOptionModalOpen, openMoveProductDialog, currentBodId, currentSectionId, openMoveDialog, movingIds, isLotSelectedForMovingProducts, hierarchy, openConfirmationDialog, deleteIds, addDataLoading, isCreateKitDisabled, isChangeSectionDisabled, selectedText, selectedItems, productIds, isSectionSelected, gridReady, tableHeight, deleteLoading, maxWidthForPinnedColumn, kitCreationError, bulkActionHighLightStatus,
        showSectionModal, createKitLoading, lotMap, quickFilterText, noOfOptionSections, bulkActionsHideUnhideStatus, startColumnOnPasteRef, openBulkEditModal, handleBulkEdit } = useBomTableActionHandlers({ bomId, tableData: (tableData as unknown as SectionAndBomItemData[]), tableRef, isOptionTable: false, getSectionBodItems, optionSection, tableMaxHeight, mainProductOptionsInfo, currentGroupWhilePastingRef, selectedRowsRef, onPricingInfoEdit: handlePricingInfoEdit, handleKitPricingUpdate, currentTabValue });

    useImperativeHandle(
        ref,
        () => {
            return tableRef;
        },
        []
    );

    const { columnOrderDetails, isColumnDataLoading } = useGetCustomisedColumnData(bomId, 'bom_pricing_tab');
    const { productFinderAccess } = useCheckAccessForProductFinderOrQuoting();
    const { isLoading: isRoundingLoading, data: bomRoundingPreset } = useGetBomRoundingPresetQuery(bomId!, {
        enabled: Boolean(bomId)
    });

    const lotsObj = useLotsObj(bomId);
    const isTableLoading = useMemo(() => (isLoading || isColumnDataLoading || isRoundingLoading || isTableLoadingAfterMutation), [isColumnDataLoading, isLoading, isRoundingLoading, isTableLoadingAfterMutation])

    useLayoutEffect(() => {
        if (!gridReady || !tableRef.current) {
            return;
        }
        const { api } = tableRef.current;
        let overlayTimeout: NodeJS.Timeout | null = null;

        if (isTableLoading) {
            api?.showLoadingOverlay();
        } else if (!isTableLoading && tableData?.length === 0) {
            overlayTimeout = setTimeout(() => {
                api?.showNoRowsOverlay();
            }, 0);
        } else {
            api?.hideOverlay();
        }
        // Cleanup function to prevent memory leaks
        return () => {
            if (overlayTimeout) {
                clearTimeout(overlayTimeout);
            }
            // Hide overlays if component unmounts or dependencies change
            api?.hideOverlay();
        };
    }, [isTableLoading, tableData, gridReady, tableRef]);

    useEffect(() => {
        setKitsExpansionObj(JSON.parse(localStorage.getItem(`${bomId}-kits`) || '{}'));
    }, [bomId, setKitsExpansionObj]);

    const handleEdit = useCallback((props: CellValueChangedEvent) => {
        const { data, column, colDef, node } = props;

        // while doing paste operation, need to prevent the spilling of data to other sections. This event is called before the 'paste end' event.
        if (currentGroupWhilePastingRef.current && currentGroupWhilePastingRef.current !== data.groupPath[0]) {
            return;
        }

        const fieldName = (colDef.field || column.getColId()) as BOM_TABLES_ENUM;
        const bodId = data.id;
        if (node.level === ROW_LEVEL.SECTION) {
            return handleSectionNameUpdate(props)
        }
        try {
            if (
                bodId && (priceRelatedCols.has(fieldName)
                    || fieldName === BOM_TABLES_ENUM.QTY)
            ) {
                fieldName === BOM_TABLES_ENUM.QTY
                    ? handleBomInfoEdit({ [fieldName]: data[fieldName], id: bodId })
                    : handlePricingInfoEdit({ ...data, id: bodId })

                if (node.level === ROW_LEVEL.COMPONENT) {
                    handleKitPricingUpdate({ onPricingInfoEdit: handlePricingInfoEdit, tableRef, type: 'edit' });
                }
            } else if (bodId && pricingBodInfoCols.has(fieldName)) {
                let payload: Partial<MainProductDataType> = { id: bodId };

                if (fieldName === BOM_TABLES_ENUM.LEAD_TIME_VALUE) {
                    payload.leadTimeMin = data.leadTimeMin;
                    payload.leadTimeMax = data.leadTimeMax;

                    if (data?.leadTimeUnit === null && !isNil(data?.leadTimeMin)) {
                        payload = { ...payload, leadTimeUnit: LEAD_TIME_UNITS.WEEKS.toLowerCase() };
                    }
                } else {
                    payload[fieldName as keyof MainProductDataType] = data[fieldName];
                }

                handlePricingInfoEdit(payload);
            } else if (bodId && bomCols.has(fieldName)) {
                const value = isString(data[fieldName]) ? data[fieldName].toUpperCase() : ''

                if (fieldName === 'manufacturer') {
                    const { isValid, sanitizedValue } = isValidCharactersForMfg(value);
                    const manufacturerValue = isValid ? value : sanitizedValue;

                    if (!isValid) {
                        node.setData({ ...data, manufacturer: sanitizedValue });
                        setSnackbarMessage(INVALID_CHARACTERS_IN_MFG_NAME);
                    }

                    handleBomInfoEdit({
                        [fieldName]: manufacturerValue,
                        id: bodId
                    });
                } else {
                    handleBomInfoEdit({
                        [fieldName]: value,
                        id: bodId
                    });
                }
            }
        } catch (error: any) {
            notifyToSentry(`(MainProductTable)${error.name}: ${error.message}\n${error.stack}`);
        } finally {
            return
        }
    }, [currentGroupWhilePastingRef, handleBomInfoEdit, handleKitPricingUpdate, handlePricingInfoEdit, handleSectionNameUpdate, setSnackbarMessage])

    const defaultTableLockedDefs: ColDef[] = useMemo(() => ([
        { field: BOM_TABLES_ENUM.ID, hide: true, resizable: false, lockPosition: 'left' },
        {
            field: 'drag',
            rowDrag: true,
            width: 20,
            resizable: false,
            pinned: 'left',
            lockPinned: true,
            editable: false,
            colSpan: (params: ColSpanParams) => (params.node?.rowPinned === 'bottom' ? 3 : 0),
            hide: false,
            lockPosition: 'left',
            cellClassRules: { ...isHoverCellClassRules }
        },
        {
            minWidth: 50,
            maxWidth: 50,
            cellStyle: getTemplateCellStyle,
            editable: false,
            cellRenderer: MenuOptions,
            headerComponent: () => (
                <Box mt={1}>
                    <TableHeaderMenuIcon color="secondary" fontSize="medium" />
                </Box>
            ),
            pinned: 'right',
            colId: 'menu',
            resizable: false,
            lockPosition: 'right',
            lockPinned: true
        }
    ]), [isHoverCellClassRules])

    const columnDef: Record<string, ColDef> = useMemo(() => ({
        [BOM_TABLES_ENUM.TYPE]: {
            headerName: 'Type',
            field: BOM_TABLES_ENUM.TYPE,
            minWidth: 80,
            editable: true,
            pinned: 'left',
            cellEditorParams: (params: ICellRendererParams) => {
                if (params?.node?.level === ROW_LEVEL.SECTION) {
                    return { maxLength: 90 }
                }
                return undefined;
            },
            cellEditor: 'agTextCellEditor',
            cellClassRules: { ...isHoverCellClassRules },
        },
        [BOM_TABLES_ENUM.QTY]: {
            headerName: 'Qty',
            field: BOM_TABLES_ENUM.QTY,
            cellDataType: 'number',
            valueGetter: getValueGetterForPricingCols,
            valueSetter: getValueSetterForMainProductPricingCols,
            cellEditor: 'agNumberCellEditor',
            cellEditorParams: {
                precision: 0,
                max: 2147483647,
                preventStepping: true
            },
            minWidth: 85,
            maxWidth: 100,
            pinned: 'left',
            type: 'rightAligned',
            aggFunc: 'addQtySum',
            editable: isSectionEditingNotAllowed,
            cellClassRules: { ...isHoverCellClassRules, ...isDisabledCellClassRules },
            cellRenderer: QtyTemplate
        },
        [BOM_TABLES_ENUM.MANUFACTURER]: {
            field: 'manufacturer',
            headerName: 'Manufacturer',
            editable: isSectionEditingNotAllowed,
            minWidth: 80,
            pinned: 'left',
            valueFormatter: 'value.toUpperCase()',
            valueGetter: valueGetterForMfg,
            cellEditor: AutocompleteEditor,
            suppressKeyboardEvent: suppressKeyboardEvent,
            cellClassRules: {
                ...isHoverCellClassRules, ...isDisabledCellClassRules
            },
            cellEditorParams: {
                options: companyMfgData?.data.map((str) => {
                    return { label: str.manufacturer_name, value: str.manufacturer_name };
                }),
                freeSolo: true,
                values: companyMfgData?.data,
                onValueChange: handleEdit,
                filterList: true,
                searchType: 'matchAny',
                allowTyping: true,
                highlightMatch: true,
                cellDataType: 'text',
                valueListMaxHeight: 100,
                valuePlaceholder: 'Search with Manufacture'
            } as IRichCellEditorParams
        },
        [BOM_TABLES_ENUM.MODEL_NO]: {
            headerName: 'Model Number',
            field: BOM_TABLES_ENUM.MODEL_NO,
            resizable: true,
            minWidth: 80,
            pinned: 'left',
            valueFormatter: 'value?.toUpperCase()',
            cellEditorParams: TextCellParams,
            cellDataType: 'text',
            editable: isSectionEditingNotAllowed,
            cellClassRules: { ...isHoverCellClassRules, ...isDisabledCellClassRules },
        },
        [BOM_TABLES_ENUM.PRODUCT_FINDER]: {
            field: BOM_TABLES_ENUM.PRODUCT_FINDER,
            minWidth: 100,
            headerComponent: () => (
                <CustomHeader isProductFinder={true} />
            ),
            pinned: 'left',
            editable: false,
            cellRenderer: productFinderAccess ? ProductFinderTemplate : ProductFinderDisabledTemplate,
            cellStyle: { textAlign: 'center', ...getTemplateCellStyle },
            cellClassRules: { ...isHoverCellClassRules, ...isDisabledCellClassRules },
        },
        [BOM_TABLES_ENUM.QUOTE_NOTES]: {
            headerName: 'Quote Notes (External)', field: BOM_TABLES_ENUM.QUOTE_NOTES, minWidth: 80, cellDataType: 'text',
            editable: isSectionEditingNotAllowed,
            cellClassRules: { ...isHoverCellClassRules, ...isDisabledCellClassRules },
        },
        [BOM_TABLES_ENUM.INTERNAL_COMMENTS]: {
            headerName: 'Internal Comments', field: BOM_TABLES_ENUM.INTERNAL_COMMENTS, minWidth: 80, cellDataType: 'text', editable: isSectionEditingNotAllowed,
            cellClassRules: { ...isHoverCellClassRules, ...isDisabledCellClassRules },
        },
        [BOM_TABLES_ENUM.SPECIFICATION]: {
            headerName: 'Specification',
            field: 'specification',
            colId: 'specification',
            editable: false,
            cellStyle: getTemplateCellStyle,
            cellRenderer: SpecificationDropdown,
            getQuickFilterText: (params: GetQuickFilterTextParams) => {
                return params?.value;
            },
            width: 204,
            minWidth: 80,
            cellClassRules: { ...isHoverCellClassRules, ...isDisabledCellClassRules },
        },
        [BOM_TABLES_ENUM.DESCRIPTION]: {
            field: BOM_TABLES_ENUM.DESCRIPTION, headerName: 'Description', minWidth: 80, cellEditorParams: TextCellParams, cellDataType: 'text',
            editable: isSectionEditingNotAllowed,
            cellClassRules: { ...isHoverCellClassRules, ...isDisabledCellClassRules },
        },
        [BOM_TABLES_ENUM.PRODUCT_TO_REPLACE]: {
            headerName: 'Product To Replace',
            field: BOM_TABLES_ENUM.PRODUCT_TO_REPLACE,
            editable: false,
            cellRenderer: ProductToReplaceTemplate,
            getQuickFilterText: () => '',
            width: 300,
            minWidth: 80,
            cellStyle: getTemplateCellStyle,
            hide: true,
            cellClassRules: { ...isHoverCellClassRules, ...isDisabledCellClassRules },
        },
        [BOM_TABLES_ENUM.LOT]: {
            headerName: 'Lot',
            colId: BOM_TABLES_ENUM.LOT,
            editable: false,
            cellRenderer: LotTemplate,
            getQuickFilterText: (params: GetQuickFilterTextParams) => {
                return params?.data?.[BOM_TABLES_ENUM.LOT_INFO]?.name;
            },
            width: 180,
            minWidth: 80,
            cellStyle: getColorHandlerForMainTable,
            tooltipValueGetter: (params) => {
                if (params.node?.level === ROW_LEVEL.SECTION) return;

                if (params?.data?.kit) return COMPONENT_LOTS_TOOLTIP_MESSAGE;
                return '';
            },
            cellClass: 'grid-cell-centered',
            cellClassRules: { ...isHoverCellClassRules, ...isDisabledCellClassRules },
        },
        [BOM_TABLES_ENUM.HISTORY]: {
            minWidth: 69,
            maxWidth: 69,
            headerComponent: () => (
                <CustomHeader isPricingHistory={true} />
            ),
            cellRenderer: (params: ICellRendererParams) => {
                if (params?.node?.rowPinned === 'bottom' || params?.data?.is_kit) {
                    return null;
                }

                if (params?.node?.level === ROW_LEVEL.SECTION) {
                    return;
                }

                function handleClick() {
                    const { category, manufacturer, model_number, id, cost, discount, discountedCost, extendedDiscountCost, sellPrice, margin, qty, extendedPrice, lotId } = params.data;
                    const { api, data, node } = params;
                    const { kit, is_kit } = data;
                    const isKitTable = is_kit;
                    const isComponentsTable = Boolean(kit) && node.level === ROW_LEVEL.COMPONENT;
                    const kitDetails = api.getRowNode(kit);

                    setPricingHistoryInfo({
                        type: category,
                        manufacturer,
                        modelNo: model_number,
                        id,
                        cost,
                        discount,
                        discountedCost,
                        extendedDiscountCost,
                        sellPrice,
                        margin,
                        qty,
                        extendedPrice,
                        lotId,
                        kitLotId: isKitTable && isComponentsTable ? kitDetails?.data?.lotId ?? undefined : undefined
                    });
                    setPricingHistoryModalOpen(true);
                }

                return (
                    <Box width="100%" alignItems="center">
                        <IconButton onClick={handleClick}>
                            <HistoryOutlinedIcon color="primary" fontSize="small" />
                        </IconButton>
                    </Box>
                );
            },
            colId: BOM_TABLES_ENUM.HISTORY,
            editable: false,
            cellStyle: (props) => {
                if (props?.node.level === 0) return;
                return { backgroundColor: 'unset' };
            },
            resizable: true,
            cellClassRules: { ...isHoverCellClassRules, ...isDisabledCellClassRules },
        },
        [BOM_TABLES_ENUM.COST]: {
            headerName: 'Cost',
            colId: BOM_TABLES_ENUM.COST,
            valueGetter: getValueGetterForPricingCols,
            valueSetter: getValueSetterForMainProductPricingCols,
            cellDataType: 'number',
            editable: (params) => (isPricingFieldsEditable(params) && isSectionEditingNotAllowed(params as CellClassParams)),
            valueFormatter: getCellAmountFormatter,
            getQuickFilterText: getCellAmountFormatter,
            cellEditor: 'agNumberCellEditor',
            cellEditorParams: pricingCellParams,
            width: 120,
            minWidth: 80,
            type: 'rightAligned',
            cellClassRules: {
                'ag-grid-disable-cell': (args) => ((!isPricingFieldsEditable(args) && !args?.node?.rowPinned) || (!args.node.group && args?.node?.level === 0)),
                'ag-grid-footer-cell': (args) => args?.node?.rowPinned === 'bottom',
                ...isHoverCellClassRules
            },
            tooltipValueGetter: (params) => pricingFieldToolTipValueGetter(params, BOM_TABLES_ENUM.COST),
        },
        [BOM_TABLES_ENUM.PER]: {
            field: BOM_TABLES_ENUM.PER,
            minWidth: 80,
            cellRenderer: PerTemplate,
            cellStyle: (args: any) => {
                const disabled = args.data.is_kit && !args?.node?.rowPinned;
                if (args.node.level === 0) {
                    return;
                }
                return { backgroundColor: disabled ? DISABLED_COLOR_HEX : 'unset', padding: '4px 0px' };
            },
            valueGetter: getValueGetterForPricingCols,
            valueSetter: getValueSetterForMainProductPricingCols,
            getQuickFilterText: (params: GetQuickFilterTextParams) => {
                return params.value;
            },
            headerComponent: () => <PerCustomHeader />,
            cellClass: 'grid-cell-content-centered',
            cellClassRules: { ...isHoverCellClassRules, ...isDisabledCellClassRules },
            editable: isSectionEditingNotAllowed
        },
        [BOM_TABLES_ENUM.DISCOUNT]: {
            headerName: 'Discount (%)',
            colId: BOM_TABLES_ENUM.DISCOUNT,
            valueSetter: getValueSetterForMainProductPricingCols,
            valueGetter: getValueGetterForPricingCols,
            editable: (isPricingFieldsEditable || isSectionEditingNotAllowed),
            valueFormatter: getCellPercentFormatter,
            getQuickFilterText: getCellPercentFormatter,
            cellRenderer: getSectionalValueTemplate,
            cellEditor: 'agNumberCellEditor',
            cellEditorParams: pricingCellParams,
            cellDataType: 'number',
            cellClassRules: {
                'ag-grid-disable-cell': (args) => ((!isPricingFieldsEditable(args) && !args?.node?.rowPinned) || (!args.node.group && args?.node?.level === 0)),
                'ag-grid-footer-cell': (args) => args?.node?.rowPinned === 'bottom',
                ...isHoverCellClassRules
            },
            width: 119,
            minWidth: 80,
            type: 'rightAligned',
            aggFunc: 'percentageDiscount',
            tooltipValueGetter: (params) => pricingFieldToolTipValueGetter(params)
        },
        [BOM_TABLES_ENUM.DISCOUNT_COST]: {
            headerName: 'Discounted Cost',
            colId: BOM_TABLES_ENUM.DISCOUNT_COST,
            valueGetter: getValueGetterForPricingCols,
            valueSetter: getValueSetterForMainProductPricingCols,
            editable: isPricingFieldsEditable || isSectionEditingNotAllowed,
            valueFormatter: getCellAmountFormatter,
            getQuickFilterText: getCellAmountFormatter,
            cellEditor: 'agNumberCellEditor',
            cellEditorParams: pricingCellParams,
            cellDataType: 'number',
            minWidth: 80,
            cellClassRules: {
                'ag-grid-disable-cell': (args) => ((!isPricingFieldsEditable(args) && !args?.node?.rowPinned) || (!args.node.group && args?.node?.level === 0)),
                'ag-grid-footer-cell': (args) => args?.node?.rowPinned === 'bottom',
                ...isHoverCellClassRules
            },
            width: 143,
            type: 'rightAligned',
            tooltipValueGetter: (params) => pricingFieldToolTipValueGetter(params)
        },
        [BOM_TABLES_ENUM.EXT_DISCOUNT_COST]: {
            headerName: 'Ext. Discounted Cost',
            colId: BOM_TABLES_ENUM.EXT_DISCOUNT_COST,
            valueGetter: getValueGetterForPricingCols,
            valueSetter: getValueSetterForMainProductPricingCols,
            editable: false,
            valueFormatter: getCellAmountFormatter,
            getQuickFilterText: getCellAmountFormatter,
            cellRenderer: getSectionalValueTemplate,
            cellEditor: 'agNumberCellEditor',
            cellEditorParams: pricingCellParams,
            cellDataType: 'number',
            minWidth: 80,
            cellClassRules: {
                'ag-grid-disable-cell': (args) => !args?.node?.rowPinned || (!args.node.group && args?.node?.level === 0),
                'ag-grid-footer-cell': (args) => args?.node?.rowPinned === 'bottom',
                ...isHoverCellClassRules
            },
            width: 143,
            type: 'rightAligned',
            tooltipValueGetter: (params) => pricingFieldToolTipValueGetter(params, BOM_TABLES_ENUM.EXT_DISCOUNT_COST),
            aggFunc: 'sumExtDisCountPrice'
        },
        [BOM_TABLES_ENUM.MARGIN]: {
            headerName: 'Margin(%)',
            colId: BOM_TABLES_ENUM.MARGIN,
            valueGetter: getValueGetterForPricingCols,
            valueSetter: getValueSetterForMainProductPricingCols,
            editable: isPricingFieldsEditable || isSectionEditingNotAllowed,
            valueFormatter: getCellPercentFormatter,
            getQuickFilterText: getCellPercentFormatter,
            cellRenderer: getSectionalValueTemplate,
            cellEditor: 'agNumberCellEditor',
            cellEditorParams: pricingCellParams,
            cellDataType: 'number',
            cellClassRules: {
                'ag-grid-disable-cell': (args) => (!isPricingFieldsEditable(args) && !args?.node?.rowPinned) || (!args.node.group && args?.node?.level === 0),
                'ag-grid-footer-cell': (args) => args?.node?.rowPinned === 'bottom',
                ...isHoverCellClassRules
            },
            width: 106,
            minWidth: 80,
            type: 'rightAligned',
            aggFunc: 'percentageMargin',
            enableCellChangeFlash: true,
            tooltipValueGetter: (params) => pricingFieldToolTipValueGetter(params)
        },
        [BOM_TABLES_ENUM.SELL_PRICE]: {
            headerName: 'Sell Price',
            colId: BOM_TABLES_ENUM.SELL_PRICE,
            valueGetter: getValueGetterForPricingCols,
            valueSetter: getValueSetterForMainProductPricingCols,
            editable: isPricingFieldsEditable || isSectionEditingNotAllowed,
            valueFormatter: getCellAmountFormatter,
            getQuickFilterText: getCellAmountFormatter,
            cellEditor: 'agNumberCellEditor',
            cellEditorParams: pricingCellParams,
            cellDataType: 'number',
            cellClassRules: {
                'ag-grid-disable-cell': (args) => (!isPricingFieldsEditable(args) && !args?.node?.rowPinned) || ((!args.node.group && args?.node?.level === 0)),
                'ag-grid-footer-cell': (args) => args?.node?.rowPinned === 'bottom',
                ...isHoverCellClassRules
            },
            width: 130,
            minWidth: 80,
            type: 'rightAligned',
            enableCellChangeFlash: true,
            tooltipValueGetter: (params) => pricingFieldToolTipValueGetter(params, BOM_TABLES_ENUM.SELL_PRICE)
        },
        [BOM_TABLES_ENUM.EXTENDED_PRICE]: {
            headerName: 'Ext. Sell Price',
            colId: BOM_TABLES_ENUM.EXTENDED_PRICE,
            field: BOM_TABLES_ENUM.ORDER,
            editable: false,
            width: 145,
            minWidth: 80,
            type: 'rightAligned',
            cellDataType: 'number',
            cellClassRules: {
                'ag-grid-disable-cell': (args) => !args?.node?.rowPinned || (!args.node.group && args?.node?.level === 0),
                'ag-grid-footer-cell': (args) => args?.node?.rowPinned === 'bottom',
                ...isHoverCellClassRules
            },
            valueGetter: getValueGetterForPricingCols,
            valueSetter: getValueSetterForMainProductPricingCols,
            valueFormatter: getExtendedSellPriceFormatter,
            getQuickFilterText: getExtendedSellPriceFormatter,
            tooltipValueGetter: (params) => pricingFieldToolTipValueGetter(params, BOM_TABLES_ENUM.EXTENDED_PRICE),
            cellRenderer: getSectionalValueTemplate,
            enableCellChangeFlash: true,
            aggFunc: 'sumExtPrice'
        },
        [BOM_TABLES_ENUM.EXT_COST]: {
            headerName: 'Ext. Cost',
            colId: BOM_TABLES_ENUM.EXT_COST,
            editable: false,
            width: 145,
            minWidth: 80,
            type: 'rightAligned',
            cellDataType: 'number',
            cellClassRules: {
                'ag-grid-disable-cell': (args) => !args?.node?.rowPinned || (!args.node.group && args?.node?.level === 0),
                'ag-grid-footer-cell': (args) => args?.node?.rowPinned === 'bottom',
                ...isHoverCellClassRules
            },
            valueGetter: getValueGetterForPricingCols,
            valueSetter: getValueSetterForMainProductPricingCols,
            valueFormatter: getExtendedCostFormatter,
            getQuickFilterText: getExtendedCostFormatter,
            tooltipValueGetter: (params) => pricingFieldToolTipValueGetter(params, BOM_TABLES_ENUM.EXT_COST),
            cellRenderer: getSectionalValueTemplate,
            enableCellChangeFlash: true,
            aggFunc: 'sumExtCost'
        },
        [BOM_TABLES_ENUM.LEAD_TIME_VALUE]: {
            headerName: 'Lead Time Value',
            colId: BOM_TABLES_ENUM.LEAD_TIME_VALUE,
            minWidth: 80,
            cellClassRules: {
                'ag-grid-disable-cell': (args) => (!isLeadTimeValueEditable(args) && !args?.node?.rowPinned) || (!args.node.group && args?.node?.level === 0),
                'ag-grid-footer-cell': (args) => args?.node?.rowPinned === 'bottom',
                ...isHoverCellClassRules
            },
            cellDataType: 'string',
            type: 'rightAligned',
            editable: isLeadTimeValueEditable || isSectionEditingNotAllowed,
            valueGetter: (params: ValueGetterParams<MainProductDataType>) => {
                const { data } = params;
                if (isNil(data)) return null;
                const range = getFormattedLeadTimeRange(data);
                return range || null;
            },
            valueSetter: (params: ValueSetterParams<MainProductDataType>) => {
                const { data, newValue } = params;
                if (!data) return false;

                const response = getValidatedLeadTimeRange(newValue);
                if (response.message) {
                    // show snackbar, either error or adjusted
                    setSnackbarMessage(response.message);
                }
                if (response.isError) return false;

                const [min, max] = response.tuple;
                if (min !== data.leadTimeMin || max !== data.leadTimeMax) {
                    data.leadTimeMin = min ?? null;
                    data.leadTimeMax = max ?? null;
                    return true;
                }
                return false;
            },
            tooltipValueGetter: (params) => {
                if (params.node?.level === ROW_LEVEL.SECTION) return;

                if (!isLeadTimeValueEditable(params)) return LEAD_TIME_VALUE_TOOLTIP_MSG;
                return '';
            }
        },
        [BOM_TABLES_ENUM.LEAD_TIME_UNIT]: {
            headerName: 'Lead Time Unit',
            colId: BOM_TABLES_ENUM.LEAD_TIME_UNIT,
            minWidth: 80,
            editable: false,
            cellStyle: { padding: '4px 0px' },
            cellRenderer: PricingLeadTimeUnitTemplate,
            getQuickFilterText: (params: GetQuickFilterTextParams) => {
                return params.value;
            },
            cellClass: 'grid-cell-content-centered',
            cellClassRules: { ...isHoverCellClassRules, ...isDisabledCellClassRules },

        },
        [BOM_TABLES_ENUM.QUOTE_NO]: {
            headerName: 'Source Quotes Number', field: BOM_TABLES_ENUM.QUOTE_NO, minWidth: 80, cellDataType: 'text', cellClassRules: { ...isHoverCellClassRules, ...isDisabledCellClassRules }, editable: isSectionEditingNotAllowed
        }
    }), [companyMfgData?.data, handleEdit, isHoverCellClassRules, productFinderAccess, setSnackbarMessage, suppressKeyboardEvent, valueGetterForMfg])

    const tableColumnDefs = useMemo<ColDef[]>(() => {
        const orderedColumns =
            columnOrderDetails &&
            columnOrderDetails.order.reduce((result: ColDef[], field: string) => {
                if (Object.hasOwn(columnDef, field)) {
                    const column = {
                        ...columnDef[field]
                    };
                    if (columnOrderDetails?.width) {
                        const widthField = field as keyof typeof columnOrderDetails.width;
                        column.width = columnOrderDetails?.width[widthField];
                        if (['manufacturer', 'model_number', 'productFinder', 'category'].includes(field) && maxWidthForPinnedColumn) {
                            column.maxWidth = maxWidthForPinnedColumn;
                        }
                    }
                    result.push(column);
                }
                return result;
            }, []);
        return [...defaultTableLockedDefs, ...(orderedColumns || [])];
    }, [columnOrderDetails, defaultTableLockedDefs, columnDef, maxWidthForPinnedColumn]);

    const handleSpecificationChange = useCallback((specificationVal: SpecificationValType, bodId: number) => {
        handleBomInfoEdit({ specification: specificationVal, id: bodId });
    }, [handleBomInfoEdit])


    const handleLotChange = useCallback((rowNode: IRowNode, rowData: MainProductComponentsDataType, { bodId, prevLotId, lotValue }: { lotValue: string; bodId: number; prevLotId: string }) => {
        if (handleLotUpdate && handleNewLot) {
            if (lotValue === 'new') {
                handleNewLot();
                return;
            }
            const lotId = lotValue === '-' ? null : Number(lotValue);
            rowNode.setData({
                ...rowData,
                [BOM_TABLES_ENUM.LOT]: lotId,
                [BOM_TABLES_ENUM.COST]: null,
                [BOM_TABLES_ENUM.DISCOUNT]: null,
                [BOM_TABLES_ENUM.DISCOUNT_COST]: null,
                [BOM_TABLES_ENUM.EXT_DISCOUNT_COST]: null,
                [BOM_TABLES_ENUM.MARGIN]: null,
                [BOM_TABLES_ENUM.SELL_PRICE]: null,
                [BOM_TABLES_ENUM.EXTENDED_PRICE]: null,
                [BOM_TABLES_ENUM.LOT_INFO]: lotId ? { ...lotsObj[lotId].lotsInfo } : lotId
            });
            const componentIds = new Set<number>();
            if (rowData.is_kit && lotId) {
                tableRef.current?.api?.forEachNode((item: IRowNode) => {
                    if (item?.data?.kit === rowData.id) {
                        componentIds?.add(item.data.id)
                    }
                })
            }
            handleLotUpdate({ bodId, prevLotId, currLotId: lotValue, componentIds });
        }
    }, [handleLotUpdate, handleNewLot, lotsObj])

    const handleProcessCellFromClipboard = useCallback((args: ProcessCellForExportParams) =>
        handleProcessCellPasteForPricing(args, startColumnOnPasteRef), [startColumnOnPasteRef]);

    const handleProcessForClipboard = useCallback((props: ProcessCellForExportParams) => {
        const fieldName = props.column.getColId();

        if (fieldName === BOM_TABLES_ENUM.SPECIFICATION || fieldName === BOM_TABLES_ENUM.LOT || fieldName === BOM_TABLES_ENUM.LEAD_TIME_UNIT) {
            return '';
        } else if (fieldName === BOM_TABLES_ENUM.EXTENDED_PRICE && props.node?.data.lotId) {
            return `Lot (${props.node?.data.lotInfo.name})`;
        }

        return props.value;
    }, [])

    const handleLeadTimeUnit = useCallback((rowNode: IRowNode, payload: Partial<MainProductDataType>) => {
        rowNode.setDataValue(BOM_TABLES_ENUM.LEAD_TIME_UNIT, payload.leadTimeUnit);
        const leadTimeRange = getFormattedLeadTimeRange(payload);
        rowNode.setDataValue(BOM_TABLES_ENUM.LEAD_TIME_VALUE, leadTimeRange);
        handlePricingInfoEdit(payload);
    }, [handlePricingInfoEdit])

    const handlePerValue = useCallback((rowNode: IRowNode, payload: Partial<MainProductDataType>) => {
        rowNode.setDataValue(BOM_TABLES_ENUM.PER, payload.per);
        handlePricingInfoEdit(payload);
    }, [handlePricingInfoEdit])

    const handleColumnMoved = useCallback(async (props: ColumnMovedEvent) => {
        const savedState = props.api.getColumnState();
        if (props.finished && props.source === 'uiColumnMoved') {
            const newColumnOrder = savedState.map((column: ColumnState) => column.colId);
            const exceptDefaultColumns = newColumnOrder.slice(defaultTableLockedDefs.length - 1);
            handleTablesReordering?.(exceptDefaultColumns);

            const newArraySet = new Set(exceptDefaultColumns);
            const sortedArray: string[] = [...exceptDefaultColumns];
            if (columnOrderDetails && columnOrderDetails.order) {
                sortedArray.push(...columnOrderDetails.order.filter((item: string) => !newArraySet.has(item)));
            }
            await saveColumnOrderEvents("bom_pricing_tab", sortedArray);
        }
    }, [columnOrderDetails, defaultTableLockedDefs.length, handleTablesReordering])

    const handleColumnResized = useCallback(async (props: ColumnResizedEvent) => {
        const colId: any = props?.column?.getColId();
        const newChangedWidth = props?.column?.getActualWidth();
        const currentWidths = columnOrderDetails?.width;
        const columnState = props.api.getColumnState();
        const ignorePinnedColumnState = columnState.slice(defaultTableLockedDefs.length - 1);
        const columnWidths = ignorePinnedColumnState.reduce((acc: any, item) => {
            acc[item.colId] = item.width;
            return acc;
        }, {});
        const newWidths = { ...currentWidths, ...columnWidths };
        handleTablesWidthChanged?.(colId, newChangedWidth);
        if (props.finished && props.source === 'uiColumnResized') {
            await saveColumnWidthEvents(newWidths, 'bom_pricing_tab');
        }
    }, [columnOrderDetails?.width, defaultTableLockedDefs.length, handleTablesWidthChanged]);

    const handlePricingModalClose = useCallback(() => {
        setPricingHistoryModalOpen(false);
    }, [])

    const getDataPath = useCallback((data: MainProductDataType | null): string[] => {
        return (data?.groupPath || []).map(String);
    }, []);

    const noRowsOverlayComponent = useCallback((props: CustomNoRowsOverlayProps) => {
        // It's safe to use the old overlay component here, as the options table won't render if it's empty.
        if (!isTableLoading && tableData.length === 0) {
            return <NoRowsOverlayWithButtons {...props} onEnterManually={onCreateSectionForEmptyTable} />
        }
        return <LoadingOverlayComponent />
    }, [isTableLoading, onCreateSectionForEmptyTable, tableData.length])

    const context = useMemo(() => {
        const actionProps = {
            isOptionTable,
            bomId,
            lotMap,
            onClickMoveSection,
            handleAddOption: onAddOption,
            noOfOptionSections,
            onProductMoveIn: onProductMoveToKit,
            onProductMoveOut: onProductMoveOutFromKit,
            onHighlight,
            onCopyProductDataToKit,
            onCheckboxChange,
            onAddBelow,
            onHide,
            onClickDelete,
            onDuplicate,
            setSelectedItems,
            selectedItems,
            disabledToolBarButton: selectedItems.length === 0,
            onCreateKit: onCreateKitWithSelectedLineItems,
            onHideMultiple,
            onMove: onMoveMultipleItems,
            onClickMultipleDelete,
            onDuplicateMultiple,
            onHighlightMultiple,
            isCreateKitDisabled,
            isChangeSectionDisabled,
            isHighlightDisabled: !(productIds || []).length,
            tableRef,
            selectedRowsRef,
            createNewRowsFromClipboard,
            addNewRowsToTheTable,
            toRemoveColumnList: [BOM_TABLES_ENUM.ADD_DEDUCT_AMT, BOM_TABLES_ENUM.PRODUCT_TO_REPLACE]
        };
        if (bomRoundingPreset?.data) return { ...actionProps, roundingBehaviourOnCell: bomRoundingPreset?.data?.rounding_behaviour, roundingPrecision: bomRoundingPreset?.data?.rounding_precision };

        return actionProps;
    }, [isOptionTable, bomId, lotMap, onClickMoveSection, onAddOption, noOfOptionSections, onProductMoveToKit, onProductMoveOutFromKit, onHighlight, onCopyProductDataToKit, onCheckboxChange, onAddBelow, onHide, onClickDelete, onDuplicate, setSelectedItems, selectedItems, onCreateKitWithSelectedLineItems, onHideMultiple, onMoveMultipleItems, onClickMultipleDelete, onDuplicateMultiple, onHighlightMultiple, isCreateKitDisabled, isChangeSectionDisabled, productIds, selectedRowsRef, createNewRowsFromClipboard, addNewRowsToTheTable, bomRoundingPreset?.data]);

    const popupParent = useMemo<HTMLElement | null>(() => {
        return document.getElementById("root");
    }, []);

    const tableOption = useMemo(() => {
        return (props: GetContextMenuItemsParams) => getTableRightClickOptions({ ...props, context: context });
    }, [context]);

    return (
        <>
            <Portal>
                <Snackbar open={Boolean(snackbarMessage)} onClose={() => setSnackbarMessage('')} message={snackbarMessage} />
            </Portal>
            {isPricingHistoryModalOpen && (
                <PricingHistoryModal
                    open={isPricingHistoryModalOpen}
                    onClose={handlePricingModalClose}
                    pricingInfo={pricingHistoryInfo}
                    roundingPrecision={bomRoundingPreset?.data?.rounding_precision || ''}
                    roundingBehaviourOnCell={bomRoundingPreset?.data?.rounding_behaviour || ''}
                    updateKitCallback={handleKitPricingCallback}
                />
            )}
            {isAddOptionModalOpen && <AddOptionModal open={isAddOptionModalOpen} onConfirm={handleAddOptionForMultipleOptions} onClose={handleToggleAddOptionModal} isAddOptionInProgress={addDataLoading} />}
            {openConfirmationDialog && (
                <DeleteConfirmationDialog
                    open={openConfirmationDialog}
                    title={isSectionSelected ? DELETE_SECTION_TITLE : deleteDialogTitle}
                    handleDialog={closeConfirmationDialog}
                    confirmAction={onDeleteBulk}
                    loading={deleteLoading}
                >
                    <Box width="40vw" my={4}>
                        <BodySmall limit={false} fontWeight="600" color="text.primary">
                            {deleteTitle(isSectionSelected)}
                        </BodySmall >
                        <BodySmall limit={false} my={1} color="text.primary">
                            <p style={{ whiteSpace: 'pre-line' }}>{deleteMessage(mainProductOptionsInfo!, deleteIds, isSectionSelected)}</p>
                        </BodySmall>
                    </Box >

                </DeleteConfirmationDialog >
            )}
            {
                openMoveDialog && (
                    <MoveSectionDialog
                        open={openMoveDialog}
                        handleDialog={toggleMoveSectionDialog}
                        movingIds={movingIds}
                        handleClearSelection={handleClearSelection}
                        currentSectionId={currentSectionId}
                        mainProductOptionsInfo={mainProductOptionsInfo}
                        isLotsPresentInBods={isLotSelectedForMovingProducts}
                        isOptionTable={false}
                        hierarchy={hierarchy}
                        setSnackbarMessage={setSnackbarMessage}
                        getSectionBodItems={getSectionBodItems}
                    />
                )
            }
            {
                openMoveProductDialog && currentBodId && currentSectionId && (
                    <MoveProductToKitDialog
                        open={openMoveProductDialog}
                        handleClose={handleMoveProductDialog}
                        currentBodId={currentBodId}
                        getTableMetaData={getTableMetaData}
                        sectionId={currentSectionId}
                        setSnackbarOpen={setSnackbarMessage}
                        isOptionTable={false}
                        onPricingInfoEdit={handlePricingInfoEdit}
                    />
                )
            }
            {
                kitCreationError && (
                    <ErrorDialog
                        open={Boolean(kitCreationError)}
                        handleDialog={() => updateKitCreationError(undefined)}
                        title="Unable to Create Kit"
                        text={KIT_ERROR[kitCreationError].text}
                        helperText={KIT_ERROR[kitCreationError].helperText}
                    />
                )
            }
            {
                openBulkEditModal && (
                    <BulkEditModal
                        open={openBulkEditModal}
                        onClose={handleBulkEdit}
                        selectedItems={selectedItems}
                        handleClearSelection={handleClearSelection}
                        setSnackbarOpen={setSnackbarMessage}
                        rounding={bomRoundingPreset?.data?.rounding_precision || ''}
                        roundingFieldName={bomRoundingPreset?.data?.rounding_behaviour || ''}
                        options={BULK_EDIT_OPTIONS as EditOptionsT[]}
                        updateKitCostCallback={handleKitPricingCallback}
                    />
                )
            }
            {showSectionModal && <SelectSectionModal open={showSectionModal} onClose={handleToggleSectionModal} isOptionTable={false} title='Create Kit' subTitle='Select the section that you would like to create your kit in.' onSubmit={onCreateKitWithoutLineItems} isLoading={createKitLoading} />}
            <MenuOptionsContext.Provider
                value={{
                    bomId,
                    isOptionTable: false,
                    noOfOptionSections,
                    handleAddOption: onAddOption,
                    onAddBelow,
                    onClickMoveSection,
                    onHide,
                    onClickDelete,
                    onDuplicate,
                    onProductMoveIn: onProductMoveToKit,
                    onProductMoveOut: onProductMoveOutFromKit,
                    onHighlight,
                    onCopyProductDataToKit,
                    onAddKitLine,
                    lotMap
                }}
            >
                <TableContext.Provider value={{ handleSpecificationChange, handleLotChange, handleLeadTimeUnit, handlePerValue, isOptionTable: false }}>
                    <Box zIndex={1} width={'100%'} position={'relative'}>
                        <CustomToolBarPanel
                            toolBarOptions={toolBaroptions as any}
                            selectedRowCount={selectedItems.length}
                            disabledToolBarButton={selectedItems.length === 0}
                            onAddDuplicates={onDuplicateMultiple}
                            onHideUnhide={onHideMultiple}
                            onMove={onMoveMultipleItems}
                            onDelete={onClickMultipleDelete}
                            onTextSearch={handleSearch}
                            onCloseBanner={onCloseBanner}
                            loading={isTableLoading}
                            onCreateKit={onCreateKitWithSelectedLineItems}
                            onHighlight={onHighlightMultiple}
                            onCreateSection={onCreateSectionForEmptyTable}
                            isCreateKitDisabled={isCreateKitDisabled}
                            isChangeSectionDisabled={isChangeSectionDisabled}
                            onBulkEdit={handleBulkEdit}
                            selectedText={selectedText || ''}
                            isHighlightDisabled={!(productIds || []).length}
                            showBorder
                            toolbarRightSection={(
                                <Box width={140}>
                                    <ViewBySelect
                                        // Passing 'Section' as it's the only value this component will have.
                                        // onChange will anyways unmount this whole component.
                                        value="Section"
                                        onChange={(e) => onChangeTableView(e.target.value as ViewOptionValues)}
                                    />
                                </Box>
                            )}
                            bulkActionsHideUnhideStatus={bulkActionsHideUnhideStatus}
                            bulkActionHighLightStatus={bulkActionHighLightStatus}
                        ></CustomToolBarPanel>
                        <Box style={{ height: `${tableHeight || 317}px` }} position="relative" className="ag-theme-quartz">
                            <AgGridReact
                                ref={tableRef}
                                rowData={tableData}
                                getRowId={getRowId}
                                gridOptions={{
                                    tooltipShowDelay: 0,
                                    tooltipInteraction: true,
                                    suppressRowClickSelection: true,
                                    enableRangeSelection: true,
                                    enableFillHandle: true,
                                    undoRedoCellEditing: true,
                                    enterNavigatesVerticallyAfterEdit: true,
                                    enterNavigatesVertically: true,
                                    rowSelection: 'multiple',
                                    stopEditingWhenCellsLoseFocus: true,
                                    suppressDragLeaveHidesColumns: true,
                                    suppressLastEmptyLineOnPaste: true,
                                    allowContextMenuWithControlKey: true,
                                    suppressMenuHide: false,
                                    rowClassRules: {
                                        'row-hide': (params: RowClassParams) => params?.data?.is_hidden,
                                        'row-highlight': (params: RowClassParams) => (params?.data?.is_kit || Boolean(params?.data?.kit) ? false : params?.data?.is_highlighted),
                                        'section-row-bold': (param: RowClassParams) => param?.node?.level === ROW_LEVEL.SECTION
                                    },
                                    getRowStyle: (params: RowClassParams) => {
                                        if (params?.node?.rowPinned) {
                                            return { backgroundColor: '#f8f8f8', fontWeight: 700 };
                                        }
                                        return undefined;
                                    },
                                    rowDragMultiRow: true,
                                    aggFuncs: aggFunctions
                                }}
                                treeData={true}
                                groupDefaultExpanded={-1}
                                enableGroupEdit={true}
                                rowHeight={ROW_HEIGHT}
                                autoGroupColumnDef={autoGroupColumnDef}
                                getContextMenuItems={tableOption}
                                context={context}
                                quickFilterText={quickFilterText}
                                columnDefs={tableColumnDefs}
                                modules={modules}
                                groupSuppressBlankHeader={true}
                                groupSelectsChildren={true}
                                getDataPath={getDataPath}
                                defaultColDef={defaultColDef}
                                onCellValueChanged={handleEdit}
                                onRowDragEnd={onRowDragEnd}
                                onSelectionChanged={onCheckboxChange}
                                processCellFromClipboard={handleProcessCellFromClipboard}
                                loadingOverlayComponent={LoadingOverlayComponent}
                                noRowsOverlayComponent={noRowsOverlayComponent}
                                processCellForClipboard={handleProcessForClipboard}
                                onGridReady={handleOnGridReady}
                                reactiveCustomComponents
                                detailRowAutoHeight
                                onRowGroupOpened={onRowGroupOpened}
                                onColumnMoved={handleColumnMoved}
                                onColumnResized={handleColumnResized}
                                onCellKeyDown={onCellKeyDown}
                                onFirstDataRendered={toggleKits}
                                processUnpinnedColumns={onHandleUnpinnedColumns}
                                onRangeSelectionChanged={handleRangeSelectionChanged}
                                onPasteEnd={handleOnPasteEnd}
                                onRowDragMove={onRowDragMove}
                                onRowDragLeave={onRowDragLeave}
                                rowDragText={rowDragTextCallback}
                                onRowDataUpdated={handleOnRowDataUpdated}
                                popupParent={popupParent}
                                onCellContextMenu={onCellContextMenu}
                                onCellFocused={onCellFocused}
                            />
                        </Box>
                    </Box>
                </TableContext.Provider >
            </MenuOptionsContext.Provider >
        </>
    );
});

export default MainProductTable;
